import React, { useState, useCallback, useRef } from 'react';
import laCountyAPI from '../utils/laCountyAPI';
import geocoding from '../utils/geocoding';
import { projectService } from '../services/projectService';
import ParcelMap from './ParcelMap';
import { exportPropertyReportToPDF } from '../utils/propertyPdfExport';
import { exportParcelBoundariesToPDF } from '../utils/parcelPdfExport';
import { Button, Card, Input, Heading, Text } from './ui';
import './AddressInput.css';

const AddressInput = ({
  structureId,
  projectId,
  currentAddress,
  onAddressUpdate,
  tone = 'dark',
}) => {
  // Use projectId if provided, otherwise fall back to structureId for backward compatibility
  const entityId = projectId || structureId;
  const [address, setAddress] = useState(currentAddress?.streetAddress || '');
  const [city, setCity] = useState(currentAddress?.city || 'Los Angeles');
  const [state, setState] = useState(currentAddress?.state || 'CA');
  const [zipCode, setZipCode] = useState(currentAddress?.zipCode || '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [propertyInfo, setPropertyInfo] = useState(null);
  const [showPropertyDetails, setShowPropertyDetails] = useState(false);
  const [parcelGeometry, setParcelGeometry] = useState(null);
  const [mapCoordinates, setMapCoordinates] = useState(
    currentAddress?.coordinates || null
  );
  const [parcelOffset, setParcelOffset] = useState({ lat: 0, lng: 0 });
  const mapRef = useRef(null);

  const handleSaveAddress = useCallback(async () => {
    if (!address.trim()) {
      setError('Please enter a street address');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Format the address
      const formattedAddress = geocoding.formatAddress({
        streetAddress: address.trim(),
        city: city.trim() || 'Los Angeles',
        state: state.trim() || 'CA',
        zipCode: zipCode.trim(),
      });

      // Geocode the address to get coordinates
      let coordinates = null;
      try {
        const geocodeResult = await geocoding.geocodeAddress(formattedAddress);
        coordinates = {
          lat: geocodeResult.lat,
          lng: geocodeResult.lng,
        };
      } catch (geocodeError) {
        console.warn(
          'Geocoding failed, saving without coordinates:',
          geocodeError
        );
        // Continue without coordinates
      }

      // Save to Firebase
      const addressData = {
        ...formattedAddress,
        coordinates,
      };

      const result = await projectService.updateProjectAddress(
        entityId,
        addressData
      );

      // Notify parent component
      if (onAddressUpdate) {
        onAddressUpdate(result.address);
      }

      setError('');
    } catch (err) {
      console.error('Error saving address:', err);
      setError(err.message || 'Failed to save address. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [address, city, state, zipCode, entityId, onAddressUpdate]);

  const handleSearchProperty = useCallback(async () => {
    if (!address.trim()) {
      setError('Please enter an address to search');
      return;
    }

    setIsLoading(true);
    setError('');
    setPropertyInfo(null);

    try {
      // Format the address
      const formattedAddress = geocoding.formatAddress({
        streetAddress: address.trim(),
        city: city.trim() || 'Los Angeles',
        state: state.trim() || 'CA',
        zipCode: zipCode.trim(),
      });

      // Step 1: Try direct address search first
      let closestParcel = null;
      let lat, lng;

      // Build a search string with just number and street name
      const addressParts = formattedAddress.streetAddress.trim().split(' ');
      const streetNumber = addressParts[0];
      const streetName = addressParts
        .slice(1)
        .join(' ')
        .replace(
          /\b(st|street|ave|avenue|blvd|boulevard|rd|road|dr|drive|ln|lane|ct|court|pl|place|way|cir|circle)\b/gi,
          ''
        )
        .trim();
      const searchAddress = `${streetNumber} ${streetName}`;

      console.log('Searching for address:', searchAddress);
      const directResults = await laCountyAPI.searchByAddress(searchAddress);

      if (directResults.success && directResults.results.length > 0) {
        // Use the first result or find the best match
        if (directResults.results.length === 1) {
          closestParcel = directResults.results[0];
        } else {
          // Find the best match by comparing addresses
          const targetNumber = parseInt(streetNumber);
          closestParcel =
            directResults.results.find((r) => {
              const resultNumber = parseInt(r.address.split(' ')[0]);
              return resultNumber === targetNumber;
            }) || directResults.results[0];
        }

        // Get coordinates from the parcel geometry
        if (closestParcel.geometry && closestParcel.geometry.rings) {
          const ring = closestParcel.geometry.rings[0];
          lng = ring.reduce((sum, point) => sum + point[0], 0) / ring.length;
          lat = ring.reduce((sum, point) => sum + point[1], 0) / ring.length;
        }
      }

      // If direct search didn't work, fall back to geocoding approach
      if (!closestParcel) {
        console.log(
          'Direct address search found no results, trying geocoding approach'
        );

        // Geocode the address
        const geocodeResult = await geocoding.geocodeAddress(formattedAddress);
        lat = geocodeResult.lat;
        lng = geocodeResult.lng;

        // Search for nearby parcels
        const nearbyParcels = await laCountyAPI.searchNearbyParcels(
          lat,
          lng,
          50
        );

        if (!nearbyParcels.parcels || nearbyParcels.parcels.length === 0) {
          setError('No properties found at this location');
          return;
        }

        // Find the closest matching parcel
        closestParcel = nearbyParcels.parcels[0];
        let minDistance = Infinity;

        for (const parcel of nearbyParcels.parcels) {
          if (parcel.geometry && parcel.geometry.rings) {
            // Calculate center of parcel
            const ring = parcel.geometry.rings[0];
            let centerLat = 0,
              centerLng = 0;
            for (const point of ring) {
              centerLng += point[0];
              centerLat += point[1];
            }
            centerLat /= ring.length;
            centerLng /= ring.length;

            // Calculate distance
            const distance = geocoding.calculateDistance(
              lat,
              lng,
              centerLat,
              centerLng
            );
            if (distance < minDistance) {
              minDistance = distance;
              closestParcel = parcel;
            }
          }
        }

        // Convert to consistent format
        closestParcel = {
          apn: closestParcel.attributes.APN,
          address: closestParcel.attributes.SitusAddress,
          useType: closestParcel.attributes.UseType,
          geometry: closestParcel.geometry,
          attributes: closestParcel.attributes,
        };
      }

      // Step 2: Use the parcel data we have
      const apn = closestParcel.apn || closestParcel.attributes?.APN;
      const attributes = closestParcel.attributes || closestParcel;

      console.log('Found parcel:', closestParcel);

      // Build property info from available data
      let propertyInfo = {
        apn: apn,
        address:
          closestParcel.address ||
          attributes.SitusAddress ||
          attributes.Address,
        useType:
          closestParcel.useType || attributes.UseType || attributes.UseCode,
        yearBuilt: attributes.YearBuilt,
        squareFeet: attributes.SQFTMain,
        units: attributes.Units,
        landValue: attributes.Roll_LandValue,
        improvementValue: attributes.Roll_ImpValue,
        totalValue:
          (attributes.Roll_LandValue || 0) + (attributes.Roll_ImpValue || 0),
      };

      // The Dynamic service is not available, so we'll use what we have from the Cache service
      console.log('Using parcel data from Cache service');

      setPropertyInfo(propertyInfo);

      // Make sure we have coordinates
      if (!lat || !lng) {
        // Try to get from parcel geometry
        if (closestParcel.geometry && closestParcel.geometry.rings) {
          const ring = closestParcel.geometry.rings[0];
          lng = ring.reduce((sum, point) => sum + point[0], 0) / ring.length;
          lat = ring.reduce((sum, point) => sum + point[1], 0) / ring.length;
        }
      }

      // Set the parcel geometry and coordinates for the map
      setParcelGeometry(closestParcel.geometry);
      setMapCoordinates(lat && lng ? { lat, lng } : null);

      // Helper function to sanitize data for Firestore (removes nested arrays)
      const sanitizeForFirestore = (obj) => {
        if (!obj) return obj;
        if (typeof obj !== 'object') return obj;

        if (Array.isArray(obj)) {
          // Check if this is an array of arrays (nested array)
          if (obj.length > 0 && Array.isArray(obj[0])) {
            // Convert nested array to array of objects
            return obj.map((item) => {
              if (Array.isArray(item)) {
                // If it's a coordinate pair, convert to object
                if (item.length === 2 && typeof item[0] === 'number') {
                  return { x: item[0], y: item[1] };
                }
                // Otherwise, stringify it
                return JSON.stringify(item);
              }
              return item;
            });
          }
          // Regular array, sanitize each element
          return obj.map(sanitizeForFirestore);
        }

        // Object - sanitize each property
        const sanitized = {};
        for (const [key, value] of Object.entries(obj)) {
          sanitized[key] = sanitizeForFirestore(value);
        }
        return sanitized;
      };

      // Save the data to Firebase
      try {
        // Sanitize all data for Firestore
        const sanitizedPropertyInfo = sanitizeForFirestore(propertyInfo);
        const sanitizedParcelGeometry = sanitizeForFirestore(
          closestParcel.geometry
        );

        await projectService.updateProjectAddress(entityId, {
          ...formattedAddress,
          coordinates: lat && lng ? { lat, lng } : null,
          propertyData: sanitizedPropertyInfo,
          parcelBoundary: sanitizedParcelGeometry,
        });
      } catch (err) {
        console.error('Error saving to Firebase:', err);
        // Try saving without parcel boundary
        await projectService.updateProjectAddress(entityId, {
          ...formattedAddress,
          coordinates: lat && lng ? { lat, lng } : null,
          propertyData: propertyInfo,
        });
      }

      setError('');
    } catch (err) {
      console.error('Error searching property:', err);
      setError(err.message || 'Failed to search property information');
    } finally {
      setIsLoading(false);
    }
  }, [address, city, state, zipCode, entityId]);

  const handleExportParcelBoundaries = useCallback(async () => {
    if (!propertyInfo) {
      setError('Please search for property information first');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Capture map image if available
      let mapImageData = null;
      if (mapRef.current && parcelGeometry) {
        try {
          mapImageData = await mapRef.current.captureMapImage();
        } catch (mapError) {
          console.warn(
            'Failed to capture map for parcel boundaries:',
            mapError
          );
        }
      }

      // Generate the parcel boundaries PDF
      const result = await exportParcelBoundariesToPDF(
        propertyInfo,
        mapImageData,
        `${address} - ${city}, ${state}`,
        entityId
      );

      if (!result.success) {
        setError(result.message || 'Failed to export parcel boundaries');
      }
    } catch (err) {
      console.error('Error exporting parcel boundaries:', err);
      setError('Failed to export parcel boundaries. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [propertyInfo, parcelGeometry, address, city, state, entityId]);

  const handleExportPDF = useCallback(async () => {
    if (!propertyInfo) {
      setError('Please search for property information first');
      return;
    }

    setIsLoading(true);
    setError(''); // Clear any previous errors

    try {
      // Update button text to show progress
      const originalError = error;
      setError('Capturing map...');

      // Capture map image if available
      let mapImageData = null;
      if (mapRef.current && parcelGeometry) {
        try {
          mapImageData = await mapRef.current.captureMapImage();
          if (!mapImageData) {
            console.warn('Map capture returned null, continuing without map');
          } else if (
            typeof mapImageData === 'string' &&
            mapImageData.startsWith('https://')
          ) {
            // If it's a Google Maps URL, fetch it through a CORS proxy
            console.log('Fetching Google Maps image through proxy...');
            try {
              // Use a public CORS proxy to fetch the image
              const proxyUrl =
                'https://corsproxy.io/?' + encodeURIComponent(mapImageData);

              const response = await fetch(proxyUrl);
              if (!response.ok) throw new Error('Failed to fetch image');

              const blob = await response.blob();
              const reader = new FileReader();

              mapImageData = await new Promise((resolve, reject) => {
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
              });

              console.log('Successfully converted Google Maps image to base64');
            } catch (fetchError) {
              console.error('Could not fetch Google Maps image:', fetchError);
              // Fall back to generating our own map
              mapImageData = null;
            }
          }
        } catch (mapError) {
          console.error('Map capture error:', mapError);
          // Continue without map image
        }
      }

      setError('Generating PDF...');

      // Generate PDF
      const result = await exportPropertyReportToPDF(
        propertyInfo,
        mapImageData,
        `${address}, ${city}, ${state}`,
        projectId // Pass projectId to save to Firebase
      );

      if (!result.success) {
        setError('Failed to generate PDF: ' + result.error);
      } else {
        setError(''); // Clear progress message on success
      }
    } catch (err) {
      console.error('Error exporting PDF:', err);
      setError('Failed to export PDF. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [propertyInfo, parcelGeometry, address, city, state, error]);

  const isDark = tone === 'dark';
  const containerCls = `address-input ${isDark ? 'address-input--dark' : 'address-input--light'}`;

  return (
    <div className={containerCls}>
      <div className="address-input__form">
        <Input
          tone={tone}
          id="street-address"
          label="Street Address"
          type="text"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="123 Main Street"
          disabled={isLoading}
        />

        <div className="address-input__row">
          <Input
            tone={tone}
            id="city"
            label="City"
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="Los Angeles"
            disabled={isLoading}
          />

          <Input
            tone={tone}
            id="state"
            label="State"
            type="text"
            value={state}
            onChange={(e) => setState(e.target.value)}
            placeholder="CA"
            maxLength="2"
            disabled={isLoading}
          />

          <Input
            tone={tone}
            id="zip"
            label="ZIP Code"
            type="text"
            value={zipCode}
            onChange={(e) => setZipCode(e.target.value)}
            placeholder="90001"
            maxLength="10"
            disabled={isLoading}
          />
        </div>

        {error && (
          <Text size="sm" className="address-input__error" role="alert">
            {error}
          </Text>
        )}

        <div className="address-input__buttons">
          <Button
            tone={tone}
            variant="primary"
            onClick={handleSaveAddress}
            disabled={isLoading || !address.trim()}
            loading={isLoading}
            fullWidth
          >
            {isLoading ? 'Saving...' : 'Save Address'}
          </Button>

          <Button
            tone={tone}
            variant="secondary"
            onClick={handleSearchProperty}
            disabled={isLoading || !address.trim()}
            fullWidth
          >
            {isLoading ? 'Searching...' : 'Search Property Info'}
          </Button>
        </div>
      </div>

      {propertyInfo && (
        <Card
          variant={isDark ? 'glass' : 'default'}
          padding="md"
          className="address-input__property"
        >
          <Heading level={4} tone={tone}>
            Property Information
          </Heading>
          <div className="address-input__property-actions">
            <Button
              tone={tone}
              variant="ghost"
              size="sm"
              onClick={() => setShowPropertyDetails(!showPropertyDetails)}
            >
              {showPropertyDetails ? 'Hide Details' : 'Show Details'}
            </Button>
            <Button
              tone={tone}
              variant="secondary"
              size="sm"
              onClick={handleExportPDF}
              disabled={isLoading}
              title="Export full property report with details"
            >
              {isLoading ? 'Generating...' : 'Export Property Report PDF'}
            </Button>
            <Button
              tone={tone}
              variant="secondary"
              size="sm"
              onClick={handleExportParcelBoundaries}
              disabled={isLoading}
              title="Export parcel boundaries map only"
            >
              {isLoading ? 'Generating...' : 'Export Parcel Boundaries PDF'}
            </Button>
          </div>

          {showPropertyDetails && (
            <div className="address-input__details">
              <div className="address-input__detail-row">
                <span className="address-input__detail-label">APN:</span>
                <span className="address-input__detail-value">
                  {laCountyAPI.formatAPN(propertyInfo.apn)}
                </span>
              </div>
              <div className="address-input__detail-row">
                <span className="address-input__detail-label">Use Type:</span>
                <span className="address-input__detail-value">
                  {propertyInfo.useType || 'N/A'}
                </span>
              </div>
              <div className="address-input__detail-row">
                <span className="address-input__detail-label">Year Built:</span>
                <span className="address-input__detail-value">
                  {propertyInfo.yearBuilt || 'N/A'}
                </span>
              </div>
              <div className="address-input__detail-row">
                <span className="address-input__detail-label">
                  Square Feet:
                </span>
                <span className="address-input__detail-value">
                  {propertyInfo.squareFeet?.toLocaleString() || 'N/A'}
                </span>
              </div>
              <div className="address-input__detail-row">
                <span className="address-input__detail-label">
                  Total Value:
                </span>
                <span className="address-input__detail-value">
                  {laCountyAPI.formatPropertyValue(propertyInfo.totalValue)}
                </span>
              </div>
              {propertyInfo.apn &&
                laCountyAPI.getAssessorMapInfo(propertyInfo.apn) && (
                  <div className="address-input__detail-row">
                    <span className="address-input__detail-label">
                      Assessor Map:
                    </span>
                    <span className="address-input__detail-value">
                      {
                        laCountyAPI.getAssessorMapInfo(propertyInfo.apn)
                          .fullReference
                      }
                      <a
                        className="address-input__detail-link"
                        href={`https://portal.assessor.lacounty.gov/parceldetail/${propertyInfo.apn}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View on Portal ↗
                      </a>
                    </span>
                  </div>
                )}
            </div>
          )}
        </Card>
      )}

      {/* Parcel Map */}
      <ParcelMap
        ref={mapRef}
        parcelGeometry={parcelGeometry}
        address={propertyInfo?.address || `${address}, ${city}, ${state}`}
        coordinates={mapCoordinates}
        height="400px"
        onBoundaryUpdate={(offset) => {
          setParcelOffset(offset);
          // Optionally save to Firebase
          console.log('Parcel offset updated:', offset);
        }}
      />
    </div>
  );
};

export default AddressInput;
